import { highlights } from '@vd/tokens';
import { isKnownShapeKind, isPinColor, isPinStyle, isRenderablePinShape, normalizeShape, pinDisplayNumber, type PinStyle, type Shape, type SnapshotShape, type TextStyle, type TextVariant } from './shapes';
import { PIN_CENTER_Y_OFFSET, PIN_RADIUS, PIN_SELECTION_PADDING } from './geometry';
import { BUG_SPRITE_TILE } from './bugSprite';
import { bugSpriteTile } from './bugSpriteImage';
import { NATURAL_H, NATURAL_W } from './naturalSpace';

/**
 * How large the bug art is drawn inside the marker.
 *
 * The same box the hand-drawn glyph occupies, so a pin does not change size
 * with its species and the fallback is the same shape as the real thing.
 */
const BUG_GLYPH = 20;

// The natural coordinate space lives in its own DOM-free module so the API
// can build a pin's shape without the canvas types (TOADO-540).
export { NATURAL_H, NATURAL_W };

/**
 * Draw one shape onto a 2D context in natural coordinates.
 *
 * `selected` changes the textBox and pin treatments. A selected textBox is
 * rendered live in a DOM overlay, while a selected pin gains a focus ring.
 *
 * The blur branch draws a placeholder (faint fill + dashed outline)
 * rather than an actual blur: on a live canvas the real blur comes from
 * a DOM overlay with backdrop-filter, and on export
 * `rasterizeAnnotatedImage` applies canvas filter+clip against the
 * image instead of calling this branch.
 */
export function drawShape(ctx: CanvasRenderingContext2D, s: Shape, selected: boolean): void {
  // Accept the exact pre-TOADO-264 marker spelling at runtime without adding
  // it to the canonical Shape union or mutating the stored snapshot. This
  // protects direct raster callers that have not hydrated through the web
  // editor's normalization path yet.
  const normalized = (s as SnapshotShape).kind === 'marker'
    ? normalizeShape(s as SnapshotShape)
    : s;
  if (!isKnownShapeKind(normalized)) return;
  s = normalized;
  if (s.kind === 'pin') {
    // Fail closed for a canonical pin record this client cannot fully
    // understand. It remains in the snapshot for a compatible future client.
    if (!isRenderablePinShape(s)) return;
    drawPin(ctx, s, selected);
    return;
  }
  if (s.kind === 'stroke') {
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.strokeStyle = highlights[s.color];
    ctx.lineWidth = s.width;
    ctx.globalAlpha = s.tool === 'highlighter' ? 0.35 : 1;
    ctx.beginPath();
    s.points.forEach((p, i) => (i === 0 ? ctx.moveTo(p[0], p[1]) : ctx.lineTo(p[0], p[1])));
    ctx.stroke();
    ctx.globalAlpha = 1;
    return;
  }
  if (s.kind === 'circle') {
    ctx.strokeStyle = highlights[s.color];
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.ellipse(s.x + s.w / 2, s.y + s.h / 2, Math.abs(s.w / 2), Math.abs(s.h / 2), 0, 0, Math.PI * 2);
    ctx.stroke();
    return;
  }
  if (s.kind === 'arrow') {
    ctx.strokeStyle = highlights[s.color];
    ctx.fillStyle = highlights[s.color];
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(s.x1, s.y1);
    ctx.lineTo(s.x2, s.y2);
    ctx.stroke();
    const angle = Math.atan2(s.y2 - s.y1, s.x2 - s.x1);
    const ah = 12;
    ctx.beginPath();
    ctx.moveTo(s.x2, s.y2);
    ctx.lineTo(s.x2 - ah * Math.cos(angle - Math.PI / 7), s.y2 - ah * Math.sin(angle - Math.PI / 7));
    ctx.lineTo(s.x2 - ah * Math.cos(angle + Math.PI / 7), s.y2 - ah * Math.sin(angle + Math.PI / 7));
    ctx.closePath();
    ctx.fill();
    return;
  }
  if (s.kind === 'redact') {
    ctx.fillStyle = '#000';
    ctx.fillRect(s.x, s.y, s.w, s.h);
    return;
  }
  if (s.kind === 'blur') {
    // Live-canvas placeholder: a faint translucent fill so the
    // shape is visible while editing. The real visual blur is
    // composed via a DOM overlay (with backdrop-filter: blur) on
    // the screen, and via canvas filter+clip on the underlying
    // image when serializing to PNG (rasterizeAnnotatedImage).
    // Without some marker on the live canvas, drag-resize handles
    // would float over invisible bounds.
    const x = Math.min(s.x, s.x + s.w);
    const y = Math.min(s.y, s.y + s.h);
    const w = Math.abs(s.w);
    const h = Math.abs(s.h);
    ctx.save();
    ctx.fillStyle = 'rgba(40, 40, 40, 0.08)';
    ctx.fillRect(x, y, w, h);
    ctx.strokeStyle = 'rgba(40, 40, 40, 0.35)';
    ctx.setLineDash([4, 3]);
    ctx.lineWidth = 1;
    ctx.strokeRect(x, y, w, h);
    ctx.restore();
    return;
  }
  if (s.kind === 'textBox') {
    // When selected, the DOM overlay renders the text live via a textarea —
    // drawing it on the canvas too would visually double it. Skip text; the
    // overlay also shows its own selection border, so we skip the dashed
    // canvas border as well.
    if (!selected) {
      drawTextBox(ctx, s);
    } else if (s.variant === 'sticky') {
      // Keep the sticky background visible underneath the textarea so the
      // edges that peek out around zero-padding look correct.
      const x = Math.min(s.x, s.x + s.w);
      const y = Math.min(s.y, s.y + s.h);
      const w = Math.abs(s.w);
      const h = Math.abs(s.h);
      ctx.save();
      ctx.fillStyle = '#FEF3C7';
      ctx.fillRect(x, y, w, h);
      ctx.strokeStyle = '#FBBF24';
      ctx.lineWidth = 1;
      ctx.strokeRect(x, y, w, h);
      ctx.restore();
    }
    return;
  }
}

type GlyphDrawing = (ctx: CanvasRenderingContext2D, x: number, cy: number) => void;

/**
 * Each listed glyph's drawing, keyed by the glyph (TOADO-413, TOADO-526). Its keys
 * are TICKET_TYPE_GLYPHS less `bug`, which the `bug` style's sprite draws, and
 * test/type-glyph.test.ts holds the two equal: no marker is offered without a
 * drawing, and none is drawn without being offered. A glyph not here (`bug`, or one
 * a newer build invented) draws the marker style instead.
 */
export const TICKET_TYPE_GLYPH_DRAWINGS: ReadonlyMap<string, GlyphDrawing> = new Map<string, GlyphDrawing>([
  ['improvement', (ctx, x, cy) => {
    // An upward arrow: better than it was.
    ctx.beginPath();
    ctx.moveTo(x, cy + 6);
    ctx.lineTo(x, cy - 6);
    ctx.moveTo(x - 4.5, cy - 1.5);
    ctx.lineTo(x, cy - 6.5);
    ctx.lineTo(x + 4.5, cy - 1.5);
    ctx.stroke();
  }],
  ['feature', (ctx, x, cy) => {
    // A five-pointed star: something new.
    ctx.beginPath();
    for (let point = 0; point < 10; point += 1) {
      const radius = point % 2 === 0 ? 7 : 3;
      const angle = -Math.PI / 2 + (point * Math.PI) / 5;
      const px = x + Math.cos(angle) * radius;
      const py = cy + Math.sin(angle) * radius;
      if (point === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
    }
    ctx.closePath();
    ctx.fill();
  }],
  ['question', (ctx, x, cy) => {
    ctx.beginPath();
    ctx.arc(x, cy - 2, 3.6, Math.PI, Math.PI * 2.35);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(x + 1.2, cy + 0.6);
    ctx.lineTo(x, cy + 3);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(x, cy + 5.6, 1.2, 0, Math.PI * 2);
    ctx.fill();
  }],
  ['wrench', (ctx, x, cy) => {
    // The head's jaw opens away from the handle, up and to the left. With the
    // gap where the handle meets it (as first drawn), it read as a magnifier
    // beside `search` (TOADO-526).
    ctx.beginPath();
    ctx.arc(x - 3, cy - 3, 3.4, Math.PI * 1.5, Math.PI * 3);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(x - 1, cy - 1);
    ctx.lineTo(x + 5.5, cy + 5.5);
    ctx.stroke();
  }],
  ['flag', (ctx, x, cy) => {
    ctx.beginPath();
    ctx.moveTo(x - 4.5, cy - 7);
    ctx.lineTo(x - 4.5, cy + 7);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(x - 4.5, cy - 6);
    ctx.lineTo(x + 5.5, cy - 3.5);
    ctx.lineTo(x - 4.5, cy - 1);
    ctx.closePath();
    ctx.fill();
  }],
  ['note', (ctx, x, cy) => {
    ctx.beginPath();
    ctx.moveTo(x - 4.5, cy - 4.5);
    ctx.lineTo(x + 4.5, cy - 4.5);
    ctx.moveTo(x - 4.5, cy);
    ctx.lineTo(x + 4.5, cy);
    ctx.moveTo(x - 4.5, cy + 4.5);
    ctx.lineTo(x + 1.5, cy + 4.5);
    ctx.stroke();
  }],
  ['dot', (ctx, x, cy) => {
    ctx.beginPath();
    ctx.arc(x, cy, 3.4, 0, Math.PI * 2);
    ctx.fill();
  }],
  // TOADO-526: the rest of TICKET_TYPE_GLYPHS, each inside the same box.
  ['warning', (ctx, x, cy) => {
    // A triangle with an exclamation mark.
    ctx.beginPath();
    ctx.moveTo(x, cy - 7);
    ctx.lineTo(x + 7, cy + 6);
    ctx.lineTo(x - 7, cy + 6);
    ctx.closePath();
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(x, cy - 2);
    ctx.lineTo(x, cy + 0.8);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(x, cy + 3.6, 1.1, 0, Math.PI * 2);
    ctx.fill();
  }],
  ['block', (ctx, x, cy) => {
    // A circle struck through: blocked.
    ctx.beginPath();
    ctx.arc(x, cy, 6.5, 0, Math.PI * 2);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(x - 4.6, cy + 4.6);
    ctx.lineTo(x + 4.6, cy - 4.6);
    ctx.stroke();
  }],
  ['bolt', (ctx, x, cy) => {
    ctx.beginPath();
    ctx.moveTo(x + 1.5, cy - 7.5);
    ctx.lineTo(x - 5, cy + 1);
    ctx.lineTo(x - 0.5, cy + 1);
    ctx.lineTo(x - 1.5, cy + 7.5);
    ctx.lineTo(x + 5, cy - 1);
    ctx.lineTo(x + 0.5, cy - 1);
    ctx.closePath();
    ctx.fill();
  }],
  ['code', (ctx, x, cy) => {
    // Two chevrons and a slash.
    ctx.beginPath();
    ctx.moveTo(x - 3, cy - 4.5);
    ctx.lineTo(x - 7, cy);
    ctx.lineTo(x - 3, cy + 4.5);
    ctx.moveTo(x + 3, cy - 4.5);
    ctx.lineTo(x + 7, cy);
    ctx.lineTo(x + 3, cy + 4.5);
    ctx.moveTo(x + 1.5, cy - 6);
    ctx.lineTo(x - 1.5, cy + 6);
    ctx.stroke();
  }],
  ['lock', (ctx, x, cy) => {
    ctx.beginPath();
    ctx.moveTo(x - 3.5, cy - 1);
    ctx.lineTo(x - 3.5, cy - 3);
    ctx.arc(x, cy - 3, 3.5, Math.PI, Math.PI * 2);
    ctx.lineTo(x + 3.5, cy - 1);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(x - 5.5, cy - 1);
    ctx.lineTo(x + 5.5, cy - 1);
    ctx.lineTo(x + 5.5, cy + 6.5);
    ctx.lineTo(x - 5.5, cy + 6.5);
    ctx.closePath();
    ctx.fill();
  }],
  ['eye', (ctx, x, cy) => {
    ctx.beginPath();
    ctx.moveTo(x - 7.5, cy);
    ctx.quadraticCurveTo(x, cy - 7, x + 7.5, cy);
    ctx.quadraticCurveTo(x, cy + 7, x - 7.5, cy);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(x, cy, 2.2, 0, Math.PI * 2);
    ctx.fill();
  }],
  ['search', (ctx, x, cy) => {
    ctx.beginPath();
    ctx.arc(x - 1.5, cy - 1.5, 4.5, 0, Math.PI * 2);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(x + 1.8, cy + 1.8);
    ctx.lineTo(x + 6.5, cy + 6.5);
    ctx.stroke();
  }],
  ['chat', (ctx, x, cy) => {
    // A speech bubble with its tail at the lower left.
    ctx.beginPath();
    ctx.moveTo(x - 6.5, cy - 5);
    ctx.lineTo(x + 6.5, cy - 5);
    ctx.lineTo(x + 6.5, cy + 2.5);
    ctx.lineTo(x - 1, cy + 2.5);
    ctx.lineTo(x - 4.5, cy + 6);
    ctx.lineTo(x - 4.5, cy + 2.5);
    ctx.lineTo(x - 6.5, cy + 2.5);
    ctx.closePath();
    ctx.stroke();
  }],
  ['pencil', (ctx, x, cy) => {
    ctx.beginPath();
    ctx.moveTo(x + 3.5, cy - 6.5);
    ctx.lineTo(x + 6.5, cy - 3.5);
    ctx.lineTo(x - 2.5, cy + 5.5);
    ctx.lineTo(x - 6.5, cy + 6.5);
    ctx.lineTo(x - 5.5, cy + 2.5);
    ctx.closePath();
    ctx.stroke();
  }],
  ['check', (ctx, x, cy) => {
    ctx.beginPath();
    ctx.moveTo(x - 6, cy + 0.5);
    ctx.lineTo(x - 2, cy + 4.5);
    ctx.lineTo(x + 6.5, cy - 4.5);
    ctx.stroke();
  }],
  ['clock', (ctx, x, cy) => {
    ctx.beginPath();
    ctx.arc(x, cy, 7, 0, Math.PI * 2);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(x, cy - 4);
    ctx.lineTo(x, cy);
    ctx.lineTo(x + 3, cy + 2);
    ctx.stroke();
  }],
  ['idea', (ctx, x, cy) => {
    // A light bulb: the glass, open at its neck, then the base.
    ctx.beginPath();
    ctx.arc(x, cy - 2.5, 5, Math.PI * 0.75, Math.PI * 2.25);
    ctx.lineTo(x + 2.2, cy + 3.8);
    ctx.lineTo(x - 2.2, cy + 3.8);
    ctx.closePath();
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(x - 2, cy + 6.3);
    ctx.lineTo(x + 2, cy + 6.3);
    ctx.stroke();
  }],
  ['heart', (ctx, x, cy) => {
    ctx.beginPath();
    ctx.moveTo(x, cy + 6);
    ctx.bezierCurveTo(x - 8, cy + 0.5, x - 6.5, cy - 7, x, cy - 3);
    ctx.bezierCurveTo(x + 6.5, cy - 7, x + 8, cy + 0.5, x, cy + 6);
    ctx.fill();
  }],
  ['user', (ctx, x, cy) => {
    ctx.beginPath();
    ctx.arc(x, cy - 3.5, 3, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(x, cy + 6.5, 6, Math.PI, Math.PI * 2);
    ctx.stroke();
  }],
  ['mobile', (ctx, x, cy) => {
    ctx.beginPath();
    ctx.moveTo(x - 4, cy - 7);
    ctx.lineTo(x + 4, cy - 7);
    ctx.lineTo(x + 4, cy + 7);
    ctx.lineTo(x - 4, cy + 7);
    ctx.closePath();
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(x - 1, cy + 4.3);
    ctx.lineTo(x + 1, cy + 4.3);
    ctx.stroke();
  }],
  ['globe', (ctx, x, cy) => {
    ctx.beginPath();
    ctx.arc(x, cy, 7, 0, Math.PI * 2);
    ctx.stroke();
    ctx.beginPath();
    ctx.ellipse(x, cy, 3, 7, 0, 0, Math.PI * 2);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(x - 7, cy);
    ctx.lineTo(x + 7, cy);
    ctx.stroke();
  }],
]);

/**
 * The shape a ticket type puts inside a marker (TOADO-413).
 *
 * Returns false for a glyph this build does not know, and the caller then draws the
 * marker's own style instead. That fallback is the point: the type set is editable
 * per workspace, so somebody adding "Tech debt" with a wrench this afternoon means
 * every installed build is older than the value it is about to be asked to draw.
 * Drawing nothing, or refusing the row, is what TOADO-297 exists to prevent.
 *
 * `bug` is not handled here on purpose. It falls through to the `bug` style below,
 * which already owns the five hand-picked species and their sprite, and a second
 * bug drawing would be two shapes for one meaning.
 *
 * Every shape is stroked in the white already set by the caller, inside the same
 * box the hand-drawn glyphs occupy, so the disc, tail, shadow and selection ring
 * are untouched by which type this is.
 */
function drawTicketTypeGlyph(
  ctx: CanvasRenderingContext2D,
  x: number,
  cy: number,
  glyph: unknown,
): boolean {
  if (typeof glyph !== 'string') return false;
  const draw = TICKET_TYPE_GLYPH_DRAWINGS.get(glyph);
  if (!draw) return false;
  draw(ctx, x, cy);
  return true;
}

/**
 * Draw a 32px pin whose x/y coordinate is its exact click-point tip.
 * Every surface uses this renderer, so the live marker and flattened bitmap
 * cannot drift. Invalid snapshot values no-op or fall back to the base pin.
 */
export function drawPin(
  ctx: CanvasRenderingContext2D,
  pin: {
    x: number;
    y: number;
    style: PinStyle | string;
    number?: number;
    /** Which bug, when the style is `bug`. Unknown values draw the generic one. */
    bugVariant?: string;
    /**
     * The ticket type's glyph, which decides the SHAPE inside the marker
     * (TOADO-413).
     *
     * Colour is deliberately not touched by it. Colour and style are
     * per-account-per-origin preferences because the point of the feature is a
     * marker that stands out against one particular site; a type that dictated
     * colour would make a bug pin invisible on a red page with no way to fix it.
     * Shape is legible against any background, so the type takes the shape and the
     * person keeps the colour.
     *
     * An unrecognised glyph draws the style's own shape rather than nothing. That
     * is the whole reason this is a separate field from `style` - see TOADO-297,
     * and note that a workspace-editable type set makes an unknown value the
     * ordinary case rather than a courtesy.
     */
    typeGlyph?: string;
    /** The body colour as `#rrggbb`; anything else draws the default. */
    color?: string;
  },
  selected: boolean = false,
): void {
  if (!Number.isFinite(pin.x) || !Number.isFinite(pin.y)) return;
  const style: PinStyle = isPinStyle(pin.style) ? pin.style : 'pin';
  const { x, y } = pin;
  const cy = y - PIN_CENTER_Y_OFFSET;
  const radius = PIN_RADIUS;

  ctx.save();
  ctx.shadowColor = 'rgba(17, 24, 39, 0.28)';
  ctx.shadowBlur = 8;
  ctx.shadowOffsetY = 3;
  // The chosen colour, or the violet every pin was before there was a
  // choice. Validated rather than trusted: `fillStyle` ignores a value it
  // cannot parse and keeps whatever the last shape set, so an unchecked
  // colour here would draw a pin in another shape's colour rather than
  // failing visibly.
  ctx.fillStyle = isPinColor(pin.color) ? pin.color : highlights.violet;

  // The small tail makes the stored coordinate visibly precise while the
  // circular body stays legible against arbitrary page and screenshot colors.
  ctx.beginPath();
  ctx.moveTo(x - 7, cy + 9);
  ctx.lineTo(x, y);
  ctx.lineTo(x + 7, cy + 9);
  ctx.closePath();
  ctx.fill();
  ctx.beginPath();
  ctx.arc(x, cy, radius, 0, Math.PI * 2);
  ctx.fill();

  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;
  ctx.strokeStyle = '#FFFFFF';
  ctx.fillStyle = '#FFFFFF';
  ctx.lineWidth = 2;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  /**
   * The ticket type's shape, when this build knows it (TOADO-413).
   *
   * `numbered-dot` wins over it, because a number is an ORDERING aid and the type
   * is a classification - two different axes, and somebody who asked for numbered
   * pins asked to be able to count them.
   */
  const drewTypeGlyph = style !== 'numbered-dot'
    && drawTicketTypeGlyph(ctx, x, cy, (pin as { typeGlyph?: unknown }).typeGlyph);

  if (drewTypeGlyph) {
    // Already drawn from the ticket's type above.
  } else if (style === 'pin') {
    /**
     * A ring around a dot, centred in the body.
     *
     * This used to be a small map pin - a stroked circle with a stick under
     * it - drawn inside a marker that is ALREADY a map pin. Two pins in one
     * 28px mark read as a smudge, and the stick pulled the glyph off centre
     * so the marker looked bottom-heavy next to the bug and the number.
     *
     * A ring around a dot says "this exact point", which is what a pin with
     * no other meaning is for, and it is the one shape that stays legible at
     * this size against an arbitrary page underneath.
     */
    ctx.beginPath();
    ctx.arc(x, cy, 6, 0, Math.PI * 2);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(x, cy, 2.5, 0, Math.PI * 2);
    ctx.fill();
  } else if (style === 'lilypad') {
    ctx.beginPath();
    ctx.arc(x, cy, 7, 0.15 * Math.PI, 1.85 * Math.PI);
    ctx.lineTo(x, cy);
    ctx.closePath();
    ctx.stroke();
  } else if (style === 'bug') {
    // The five hand-picked species (TOADO-296). One sprite, one tile, drawn
    // into the same box the hand-drawn glyph occupies, so the disc, the tail,
    // the shadow and the selection ring are untouched by which marker this is.
    const tile = bugSpriteTile((pin as { bugVariant?: unknown }).bugVariant);
    if (tile) {
      ctx.drawImage(
        tile.image,
        tile.sx, 0, BUG_SPRITE_TILE, BUG_SPRITE_TILE,
        x - BUG_GLYPH / 2, cy - BUG_GLYPH / 2, BUG_GLYPH, BUG_GLYPH,
      );
    } else {
    // No tile: the sprite has not decoded, cannot in this environment, or the
    // variant was written by a client that knows one this build does not. All
    // three draw the generic bug rather than an empty disc.
    ctx.beginPath();
    ctx.ellipse(x, cy + 1, 5, 7, 0, 0, Math.PI * 2);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(x, cy - 6);
    ctx.lineTo(x, cy + 8);
    ctx.moveTo(x - 5, cy - 2);
    ctx.lineTo(x - 8, cy - 5);
    ctx.moveTo(x + 5, cy - 2);
    ctx.lineTo(x + 8, cy - 5);
    ctx.moveTo(x - 5, cy + 3);
    ctx.lineTo(x - 8, cy + 6);
    ctx.moveTo(x + 5, cy + 3);
    ctx.lineTo(x + 8, cy + 6);
    ctx.stroke();
    }
  } else if (style === 'fly') {
    ctx.beginPath();
    ctx.ellipse(x - 4, cy - 2, 5, 3, -0.65, 0, Math.PI * 2);
    ctx.stroke();
    // Each wing is its own subpath. Adding both ellipses to one current
    // subpath makes Canvas connect the first ellipse's endpoint to the
    // second ellipse's start with an unintended diagonal stroke.
    ctx.beginPath();
    ctx.ellipse(x + 4, cy - 2, 5, 3, 0.65, 0, Math.PI * 2);
    ctx.stroke();
    ctx.beginPath();
    ctx.ellipse(x, cy + 3, 2.5, 6, 0, 0, Math.PI * 2);
    ctx.fill();
  } else {
    const value = pinDisplayNumber(pin.number);
    ctx.font = '700 13px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(String(value), x, cy + 0.5);
  }

  if (selected) {
    ctx.shadowColor = 'transparent';
    ctx.strokeStyle = '#FFFFFF';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(x, cy, radius + PIN_SELECTION_PADDING, 0, Math.PI * 2);
    ctx.stroke();
  }
  ctx.restore();
}


export function drawTextBox(
  ctx: CanvasRenderingContext2D,
  s: { x: number; y: number; w: number; h: number; text: string; variant: TextVariant; style: TextStyle },
): void {
  const x = Math.min(s.x, s.x + s.w);
  const y = Math.min(s.y, s.y + s.h);
  const w = Math.abs(s.w);
  const h = Math.abs(s.h);

  if (!s.text) {
    // In-progress drag (or an empty-text box): render as a blue marquee so
    // the user sees the rectangle expand from click-point to release-point.
    // Matches the selection overlay's blue so creation flows seamlessly into
    // selection when pointer is released.
    ctx.save();
    ctx.fillStyle = 'rgba(59,130,246,0.08)';
    ctx.fillRect(x, y, w, h);
    ctx.setLineDash([6, 4]);
    ctx.strokeStyle = 'rgba(59,130,246,0.85)';
    ctx.lineWidth = 2;
    ctx.strokeRect(x, y, w, h);
    ctx.restore();
    return;
  }

  if (s.variant === 'sticky') {
    ctx.save();
    ctx.fillStyle = '#FEF3C7';
    ctx.fillRect(x, y, w, h);
    ctx.strokeStyle = '#FBBF24';
    ctx.lineWidth = 1;
    ctx.strokeRect(x, y, w, h);
    ctx.restore();
  }

  ctx.save();
  ctx.font = TEXT_BOX_FONT;
  ctx.textBaseline = 'top';

  for (const { text: line, x: tx, y: ty } of textBoxLines(ctx, s)) {
    if (s.style === 'red-outline') {
      ctx.strokeStyle = '#FFFFFF';
      ctx.lineWidth = 4;
      ctx.lineJoin = 'round';
      ctx.strokeText(line, tx, ty);
      ctx.fillStyle = '#EF4444';
      ctx.fillText(line, tx, ty);
    } else if (s.style === 'red') {
      ctx.fillStyle = '#EF4444';
      ctx.fillText(line, tx, ty);
    } else if (s.style === 'black') {
      ctx.fillStyle = '#111';
      ctx.fillText(line, tx, ty);
    } else {
      ctx.save();
      ctx.shadowColor = 'rgba(0,0,0,0.85)';
      ctx.shadowBlur = 4;
      ctx.fillStyle = '#FFFFFF';
      ctx.fillText(line, tx, ty);
      ctx.restore();
    }
  }
  ctx.restore();
}

const TEXT_BOX_FONT = '600 18px Inter, sans-serif';

/**
 * The lines a text box draws and where: wrapped to the box, stopped at its
 * foot. A word wider than the box is not broken, so a line can run past the
 * box's right edge; `width` says how far. Shared by `drawTextBox` and the
 * crop of a flattened picture (`drawnArea`, TOADO-716), so the crop keeps
 * all of what is drawn.
 */
export function textBoxLines(
  ctx: CanvasRenderingContext2D,
  s: { x: number; y: number; w: number; h: number; text: string },
): Array<{ text: string; x: number; y: number; width: number }> {
  const pad = 10;
  const lineHeight = 22;
  const x = Math.min(s.x, s.x + s.w);
  const y = Math.min(s.y, s.y + s.h);
  const bottom = y + Math.abs(s.h);
  ctx.save();
  ctx.font = TEXT_BOX_FONT;
  const out: Array<{ text: string; x: number; y: number; width: number }> = [];
  const lines = wrapText(ctx, s.text, Math.abs(s.w) - pad * 2);
  for (let i = 0; i < lines.length; i++) {
    const ty = y + pad + i * lineHeight;
    if (ty + lineHeight > bottom) break;
    out.push({ text: lines[i]!, x: x + pad, y: ty, width: ctx.measureText(lines[i]!).width });
  }
  ctx.restore();
  return out;
}

/**
 * Greedy word wrap against ctx.measureText. Splits on newlines first,
 * then packs words per line up to maxWidth. A single word wider than
 * maxWidth is emitted on its own line rather than broken mid-word.
 */
export function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const out: string[] = [];
  const paragraphs = text.split('\n');
  for (const para of paragraphs) {
    const words = para.split(/\s+/);
    let line = '';
    for (const w of words) {
      const test = line ? line + ' ' + w : w;
      if (ctx.measureText(test).width > maxWidth && line) {
        out.push(line);
        line = w;
      } else {
        line = test;
      }
    }
    if (line) out.push(line);
  }
  return out;
}
